import React, { useState, useEffect, useRef } from 'react';
import { listKnowledge, createKnowledge, updateKnowledge, deleteKnowledge } from '../../api/aiKnowledge';

// ── Icons ──────────────────────────────────────────────────────
const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="11"/>
    <line x1="8" y1="15" x2="8" y2="15" strokeWidth="3"/><line x1="16" y1="15" x2="16" y2="15" strokeWidth="3"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

// ── Modal ──────────────────────────────────────────────────────
function DocModal({ doc, onClose, onSave }) {
  const [titre, setTitre] = useState(doc?.titre || '');
  const [contenu, setContenu] = useState(doc?.contenu || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!titre.trim() || !contenu.trim()) {
      setError('Le titre et le contenu sont requis.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ titre: titre.trim(), contenu: contenu.trim() });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <h3>{doc ? 'Modifier le document' : 'Nouveau document'}</h3>
          <button className="ai-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ai-modal-body">
          {error && <div className="feedback-bar feedback-error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field">
            <label>Titre du document <span>*</span></label>
            <div className="input-wrap">
              <input
                type="text"
                placeholder="ex: Safety Policy, Horaires Shuttle..."
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <div className="field">
            <label>Contenu <span>*</span></label>
            <textarea
              className="ai-textarea"
              placeholder="Collez ici le texte complet du document officiel..."
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              rows={12}
            />
          </div>
        </div>
        <div className="ai-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sauvegarde...' : (doc ? 'Enregistrer' : 'Créer')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────
export default function AiKnowledge() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalDoc, setModalDoc] = useState(null);    // null = closed, {} = new, {id,...} = edit
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const confirmRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listKnowledge();
      setDocs(data);
    } catch (e) {
      setError('Impossible de charger la base de connaissance.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleNew = () => { setModalDoc({}); setModalOpen(true); };
  const handleEdit = (doc) => { setModalDoc(doc); setModalOpen(true); };
  const handleCloseModal = () => { setModalOpen(false); setModalDoc(null); };

  const handleSave = async (payload) => {
    if (modalDoc?.id) {
      await updateKnowledge(modalDoc.id, payload);
    } else {
      await createKnowledge(payload);
    }
    await load();
  };

  const handleToggle = async (doc) => {
    setTogglingId(doc.id);
    try {
      await updateKnowledge(doc.id, { actif: !doc.actif });
      setDocs((prev) => prev.map((d) => d.id === doc.id ? { ...d, actif: !d.actif } : d));
    } catch { /* ignore */ }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ? Cette action est irréversible.')) return;
    setDeletingId(id);
    try {
      await deleteKnowledge(id);
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  return (
    <div className="page">
      <div className="ai-knowledge-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="ai-knowledge-icon"><BotIcon /></div>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Base de Connaissance — Assistant IA</h1>
            <p style={{ fontSize: 12, color: '#888', margin: 0, marginTop: 2 }}>
              Ces documents constituent la connaissance de l'assistant virtuel étudiant.
            </p>
          </div>
        </div>
        <button className="btn-primary" onClick={handleNew} id="ai-add-doc-btn">
          <PlusIcon /> Ajouter un document
        </button>
      </div>

      {error && <div className="feedback-bar feedback-error">{error}</div>}

      {loading ? (
        <div className="table-loading">Chargement de la base de connaissance…</div>
      ) : docs.length === 0 ? (
        <div className="table-empty">
          Aucun document. Cliquez sur "Ajouter un document" pour commencer.
        </div>
      ) : (
        <div className="ai-docs-list">
          {docs.map((doc) => (
            <div key={doc.id} className={`ai-doc-card${doc.actif ? '' : ' ai-doc-inactive'}`}>
              <div className="ai-doc-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  <span className={`tag ${doc.actif ? 'tag-green' : 'tag-gray'}`}>
                    {doc.actif ? '✅ Actif' : '⏸ Inactif'}
                  </span>
                  <h2 className="ai-doc-title">{doc.titre}</h2>
                </div>
                <div className="actions-cell">
                  <button
                    className="action-btn"
                    title={doc.actif ? 'Désactiver' : 'Activer'}
                    onClick={() => handleToggle(doc)}
                    disabled={togglingId === doc.id}
                    id={`ai-toggle-${doc.id}`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {doc.actif
                        ? <><rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="16" cy="12" r="3" fill="currentColor"/></>
                        : <><rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="8" cy="12" r="3" fill="currentColor"/></>
                      }
                    </svg>
                  </button>
                  <button
                    className="action-btn"
                    title="Modifier"
                    onClick={() => handleEdit(doc)}
                    id={`ai-edit-${doc.id}`}
                  >
                    <EditIcon />
                  </button>
                  <button
                    className="action-btn danger"
                    title="Supprimer"
                    onClick={() => handleDelete(doc.id)}
                    disabled={deletingId === doc.id}
                    id={`ai-delete-${doc.id}`}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
              <p className="ai-doc-preview">
                {doc.contenu.length > 220 ? doc.contenu.slice(0, 220) + '…' : doc.contenu}
              </p>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <DocModal
          doc={modalDoc?.id ? modalDoc : null}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
